import React, { useContext } from "react";
import { Accordion, Card, Button, useAccordionButton } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import type { NurseryGroup, NurseryLocation } from "../types/nurseryGroup";
import type { Nursery } from "../types/auth";
import { PlusIcon, EditIcon, DeleteIcon, ClearIcon } from "../components/icons/SettingsIcons";
import { AccordionContext } from "react-bootstrap";

interface CustomToggleProps {
  children: React.ReactNode;
  eventKey: string;
}

const CustomToggle: React.FC<CustomToggleProps> = ({ children, eventKey }) => {
  const { activeEventKey } = useContext(AccordionContext);
  const decoratedOnClick = useAccordionButton(eventKey);
  const isCurrentEventKey = activeEventKey === eventKey;

  return (
    <div
      className={`accordion-button custom-toggle ${!isCurrentEventKey ? "collapsed" : ""}`}
      onClick={decoratedOnClick}
      role="button"
      style={{ cursor: "pointer" }}
    >
      {children}
    </div>
  );
};

interface HierarchyTreeProps {
  groups: NurseryGroup[];
  locations: NurseryLocation[];
  allNurseries: Nursery[];
  onCreateGroup: () => void;
  onCreateLocation: (groupId: number) => void;
  onEditGroup: (group: NurseryGroup) => void;
  onEditLocation: (location: NurseryLocation) => void;
  onEditNursery: (nursery: Nursery) => void;
  onAssignNursery: (location: NurseryLocation) => void;
  onDeleteGroup: (groupId: number) => void;
  onDeleteLocation: (locationId: number, groupId: number) => void;
  onUnassignNursery: (nursery: Nursery) => void;
}

const HierarchyTree: React.FC<HierarchyTreeProps> = ({
  groups,
  locations,
  allNurseries,
  onCreateGroup,
  onCreateLocation,
  onEditGroup,
  onEditLocation,
  onEditNursery,
  onAssignNursery,
  onDeleteGroup,
  onDeleteLocation,
  onUnassignNursery,
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'hierarchyTree' });
  const getLocationsForGroup = (groupId: number) =>
    locations.filter((l) => l.nursery_group_id === groupId);

  const getNurseriesForLocation = (locationId: number) =>
    allNurseries.filter((c) => c.nursery_location_id === locationId);

  return (
    <>
      <div className="d-flex gap-2 mb-4">
        <Button
          variant="primary"
          size="sm"
          onClick={onCreateGroup}
        >
          <PlusIcon /> {t('addGroup')}
        </Button>
      </div>

      <Accordion defaultActiveKey="0" className="hierarchy-tree">
        {groups.map((group, idx) => {
          const locationsForGroup = getLocationsForGroup(group.id);
          return (
            <Accordion.Item
              eventKey={idx.toString()}
              key={group.id}
              className="border-0 shadow-sm mb-3 rounded overflow-hidden"
            >
              <div className="group-node-header d-flex align-items-center justify-content-between bg-white border-bottom pe-3">
                <CustomToggle eventKey={idx.toString()}>
                  <div className="d-flex align-items-center">
                    <div className="node-indicator primary me-3"></div>
                    <div>
                      <h6 className="mb-0 fw-bold">{group.name} {t('groupSuffix')}</h6>
                    </div>
                  </div>
                </CustomToggle>
                <div className="d-flex gap-2">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-1 text-primary"
                    onClick={() => onEditGroup(group)}
                  >
                    <EditIcon />
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-1 text-danger"
                    onClick={() => onDeleteGroup(group.id)}
                  >
                    <DeleteIcon />
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => onCreateLocation(group.id)}
                  >
                    <PlusIcon /> {t('addLocation')}
                  </Button>
                </div>
              </div>
              <Accordion.Body className="bg-light ps-5">
                {locationsForGroup.map((loc) => (
                  <Card
                    key={loc.id}
                    className="border-0 shadow-sm mb-3 rounded overflow-hidden"
                  >
                    <Card.Body className="py-2 px-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="d-flex align-items-center">
                          <div className="node-indicator info me-3"></div>
                          <div>
                            <span className="fw-bold d-block">
                              {loc.name} {t('locationSuffix')}
                            </span>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-success"
                            title={t('assignNursery')}
                            onClick={() => onAssignNursery(loc)}
                          >
                            <PlusIcon />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-primary"
                            onClick={() => onEditLocation(loc)}
                          >
                            <EditIcon />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-danger"
                            onClick={() => onDeleteLocation(loc.id, group.id)}
                            disabled={locationsForGroup.length <= 1}
                          >
                            <DeleteIcon />
                          </Button>
                        </div>
                      </div>
                      <div className="ps-4">
                        {getNurseriesForLocation(loc.id).map((comp) => {
                          const nurseriesForLocation = getNurseriesForLocation(loc.id);
                          return (
                            <div
                              key={comp.id}
                              className="d-flex align-items-center justify-content-between mb-2"
                            >
                              <div className="d-flex align-items-center">
                                <div
                                  className="node-indicator success me-2"
                                  style={{ height: "16px", width: "3px" }}
                                ></div>
                                <span className="small text-black">
                                  {comp.name} ({comp.nursery_id})
                                </span>
                              </div>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-primary opacity-75"
                                  title={t('editNursery')}
                                  onClick={() => onEditNursery(comp)}
                                >
                                  <EditIcon />
                                </Button>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-danger opacity-75"
                                  title={t('unassignNursery')}
                                  onClick={() => onUnassignNursery(comp)}
                                  disabled={nurseriesForLocation.length <= 1}
                                >
                                  <ClearIcon />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                        {getNurseriesForLocation(loc.id).length === 0 && (
                          <span className="small text-muted italic">
                            {t('noNurseriesAssigned')}
                          </span>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>

      <style>{`
        .hierarchy-tree .accordion-button:not(.collapsed) {
          background-color: rgba(102, 126, 234, 0.05);
          color: #667eea;
        }
        .custom-toggle {
          flex-grow: 1;
          padding: 1rem 1.25rem;
          border: none;
          background: none;
          text-align: left;
          display: flex;
          align-items: center;
        }
        .custom-toggle:after {
          flex-shrink: 0;
          width: 1.25rem;
          height: 1.25rem;
          margin-left: auto;
          content: "";
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23212529'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-size: 1.25rem;
          transition: transform .2s ease-in-out;
        }
        .custom-toggle:not(.collapsed):after {
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%230c63e4'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
          transform: rotate(-180deg);
        }
        .node-indicator {
          width: 4px;
          height: 24px;
          border-radius: 2px;
        }
        .node-indicator.primary { background-color: #667eea; }
        .node-indicator.info { background-color: #17a2b8; }
        .node-indicator.success { background-color: #28a745; }
      `}</style>
    </>
  );
};

export default HierarchyTree;